import React, { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import ReactQuill from "react-quill-new";
import { makeQuillModules } from "./quillConfig";

const EMOJIS = [
  "😀","😃","😄","😁","😆","😅","😂","🤣","😊","😇",
  "🙂","🙃","😉","😌","😍","🥰","😘","😗","😙","😚",
  "😋","😛","😝","😜","🤪","🤨","🧐","🤓","😎","🤩",
  "😏","😒","😞","😔","😟","😕","🙁","☹️","😣","😖",
  "😫","😩","🥺","😢","😭","😤","😠","😡","🤬","🤯",
  "😳","🥵","🥶","😱","😨","😰","😥","😓","🤗","🤔",
  "👍","👎","👏","🙌","👐","🤲","🤝","🙏","✌️","🤞",
  "❤️","🧡","💛","💚","💙","💜","🖤","🤍","🤎","💔",
  "⭐","🌟","✨","💥","🔥","🎉","🎊","🎈","🏆","🥇",
  "😺","🐶","🐱","🐭","🐹","🐰","🦊","🐻","🐼","🐨",
  "🍎","🍊","🍋","🍇","🍓","🍒","🍑","🥭","🍍","🥝",
  "🌸","🌺","🌻","🌹","🌷","🌱","🌿","🍃","🍀","🌴",
  "🚀","✈️","🚂","🚗","⚽","🏀","🎸","🎮","💻","📱",
];
type Props = {
  value: string;
  onChange: (val: string) => void;

  className?: string;
  onClearError?: () => void;

  storage?: string;        // so different pages can store in different folders
  modulesOverride?: any;   // optional per-place overrides

  // Set HTML content imperatively once the editor mounts (handles tables / resize attrs
  // that ReactQuill's controlled `value` prop cannot parse correctly).
  // Pass `show && data?.description ? normalizedHtml : undefined` — effect only runs
  // when this string actually changes (React compares by value, not reference).
  initialHtml?: string;
};

const CommonQuill = forwardRef<ReactQuill, Props>(
  ({ value, onChange, className = "editor", onClearError, storage = "EVENTS", modulesOverride, initialHtml }, ref) => {
    const [showEmoji, setShowEmoji] = useState(false);
    const [emojiPos, setEmojiPos] = useState({ top: 0, left: 0 });
    const emojiPickerRef = useRef<HTMLDivElement>(null);

    const modules = useMemo(() => {
      const base = makeQuillModules(ref as any, storage);
      return modulesOverride ? { ...base, ...modulesOverride } : base;
    }, [ref, storage, modulesOverride]);

    useEffect(() => {
      if (!initialHtml) return;

      let cancelled = false;

      const trySet = () => {
        if (cancelled) return;
        const editor = (ref as React.RefObject<ReactQuill>)?.current?.getEditor?.();
        if (!editor) {
          requestAnimationFrame(trySet); // editor not mounted yet — wait one frame
          return;
        }
        // Use setContents (not setText("") + updateContents): the old approach
        // left the editor's initial trailing "\n" in place and inserted the
        // converted HTML before it, adding one extra trailing newline on every
        // load → an extra blank space/paragraph accumulated each time data was
        // saved. setContents replaces the whole document and normalizes to a
        // single trailing newline.
        const delta = editor.clipboard.convert({ html: initialHtml });
        editor.setContents(delta, "silent");
        editor.setSelection(editor.getLength(), 0, "silent");
      };

      requestAnimationFrame(trySet);
      return () => { cancelled = true; };
    }, [initialHtml, ref]);

    // Prevent auto-scroll when the table picker opens.
    // quill-table-better's TableToolbar calls quill.focus() on every toolbar
    // click which triggers scrollSelectionIntoView(). That method scrolls the
    // nearest overflow:auto ancestor (e.g. Offcanvas body), NOT window, so
    // saving window.scrollY alone was ineffective. We find the actual scroll
    // container, save its scrollTop, and restore both in setTimeout(0) — which
    // runs after all synchronous click handlers finish. Scroll is only blocked
    // while the picker is opening (ql-hidden); row/column selection is left
    // untouched so the viewport follows the newly inserted table.
    useEffect(() => {
      let cancelled = false;
      let removeListener: (() => void) | null = null;

      const setup = () => {
        if (cancelled) return;
        const editor = (ref as React.RefObject<ReactQuill>)?.current?.getEditor?.();
        if (!editor) {
          requestAnimationFrame(setup);
          return;
        }
        const toolbar = (editor as any).getModule("toolbar");
        const button: HTMLElement | null =
          toolbar?.container?.querySelector("button.ql-table-better");
        if (!button) return;

        // Find the nearest scrollable ancestor of the editor container.
        // quill.focus() → scrollSelectionIntoView() scrolls this element,
        // NOT window, when the editor lives inside an Offcanvas / modal.
        const findScrollContainer = (): HTMLElement | null => {
          let node: HTMLElement | null = editor.container;
          while (node) {
            node = node.parentElement;
            if (!node || node === document.documentElement) break;
            const { overflow, overflowY } = getComputedStyle(node);
            if (
              /auto|scroll/.test(overflow + overflowY) &&
              node.scrollHeight > node.clientHeight
            ) {
              return node;
            }
          }
          return null;
        };

        const onClick = (e: MouseEvent) => {
          const picker = button.querySelector(".ql-table-select-container");
          // Only block scroll while the picker is opening (hidden → visible).
          // When the picker is visible the user is selecting rows/columns —
          // allow the scroll so the viewport follows the inserted table.
          if (!picker?.classList.contains("ql-hidden")) return;

          const scrollEl = findScrollContainer();
          const savedWindowY = window.scrollY;
          const savedContainerTop = scrollEl?.scrollTop ?? 0;

          setTimeout(() => {
            window.scrollTo(0, savedWindowY);
            if (scrollEl) scrollEl.scrollTop = savedContainerTop;
          }, 0);
        };

        // Use capture so our handler runs before the toolbar's click handler
        button.addEventListener("click", onClick, true);
        removeListener = () => button.removeEventListener("click", onClick, true);
      };

      requestAnimationFrame(setup);
      return () => {
        cancelled = true;
        removeListener?.();
      };
    }, [ref]);

    // ── Emoji picker: intercept the .ql-emoji toolbar button ──────────────
    useEffect(() => {
      let cancelled = false;
      let cleanup: (() => void) | null = null;

      const setup = () => {
        if (cancelled) return;
        const editor = (ref as React.RefObject<ReactQuill>)?.current?.getEditor?.();
        if (!editor) { requestAnimationFrame(setup); return; }

        const toolbar = (editor as any).getModule("toolbar");
        const btn: HTMLElement | null =
          toolbar?.container?.querySelector("button.ql-emoji");
        if (!btn) return;

        const handleClick = (e: MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
          const rect = btn.getBoundingClientRect();
          setEmojiPos({ top: rect.bottom + 4, left: rect.left });
          setShowEmoji((prev) => !prev);
        };

        btn.addEventListener("click", handleClick, true);
        cleanup = () => btn.removeEventListener("click", handleClick, true);
      };

      requestAnimationFrame(setup);
      return () => { cancelled = true; cleanup?.(); };
    }, [ref]);

    // Close emoji picker on outside click
    useEffect(() => {
      if (!showEmoji) return;
      const handler = (e: MouseEvent) => {
        if (emojiPickerRef.current && !emojiPickerRef.current.contains(e.target as Node)) {
          setShowEmoji(false);
        }
      };
      document.addEventListener("mousedown", handler);
      return () => document.removeEventListener("mousedown", handler);
    }, [showEmoji]);

    const insertEmoji = (emoji: string) => {
      const editor = (ref as React.RefObject<ReactQuill>)?.current?.getEditor?.();
      if (!editor) return;
      const range = editor.getSelection(true) ?? { index: editor.getLength() - 1, length: 0 };
      editor.insertText(range.index, emoji, "user");
      editor.setSelection(range.index + emoji.length, 0, "silent");
      setShowEmoji(false);
    };

    return (
      <>
        <ReactQuill
          ref={ref as any}
          theme="snow"
          value={value}
          onChange={(val) => {
            onChange(val);
            onClearError?.();
          }}
          className={className}
          modules={modules}
        />

        {showEmoji && (
          <div
            ref={emojiPickerRef}
            style={{
              position: "fixed",
              top: emojiPos.top,
              left: emojiPos.left,
              zIndex: 9999,
              background: "#fff",
              border: "1px solid #d0d0d0",
              borderRadius: "8px",
              padding: "8px",
              boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
              display: "grid",
              gridTemplateColumns: "repeat(10, 1fr)",
              gap: "2px",
              maxWidth: "340px",
              maxHeight: "240px",
              overflowY: "auto",
            }}
          >
            {EMOJIS.map((emoji) => (
              <button
                key={emoji}
                onMouseDown={(e) => {
                  e.preventDefault();   // keep editor focus
                  insertEmoji(emoji);
                }}
                style={{
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  fontSize: "20px",
                  padding: "2px",
                  lineHeight: 1,
                  borderRadius: "4px",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#f0f0f0")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
              >
                {emoji}
              </button>
            ))}
          </div>
        )}
      </>
    );
  }
);

CommonQuill.displayName = "CommonQuill";
export default CommonQuill;
